import { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, DialogFooter, Loader } from "@/ui/atoms";
import { FormField } from "@/ui/molecules";

export const KIND_OPTIONS = [
    { value: "asset", label: "Asset" },
    { value: "investment", label: "Investment" },
    { value: "entity", label: "Entity" },
];

export type TPropertyCodeDraft = {
    kind: string;
    property_code: string;
    property_name: string;
    is_active: boolean;
};

export type TPropertyCodeFormModalProps = {
    open: boolean;
    mode: "create" | "edit";
    initial?: TPropertyCodeDraft;
    isSaving?: boolean;
    error?: string | null;
    onSubmit: (draft: TPropertyCodeDraft) => void;
    onClose: () => void;
};

const emptyDraft = (): TPropertyCodeDraft => ({
    kind: "entity",
    property_code: "",
    property_name: "",
    is_active: true,
});

export function PropertyCodeFormModal({
    open,
    mode,
    initial,
    isSaving = false,
    error,
    onSubmit,
    onClose,
}: TPropertyCodeFormModalProps) {
    const [draft, setDraft] = useState<TPropertyCodeDraft>(initial ?? emptyDraft());

    // Re-seed the form as the modal opens, and only then.
    //
    // The caller builds `initial` as an inline object literal, so every one of its
    // renders hands us a fresh identity for unchanged data. Re-seeding on that
    // identity would reset the form underneath the user: a table refetch or an
    // `isSaving` flip between their click and their save silently discarded the
    // edit, so saving wrote back the value the row already had.
    const wasOpen = useRef(open);
    useEffect(() => {
        if (open && !wasOpen.current) setDraft(initial ?? emptyDraft());
        wasOpen.current = open;
    }, [open, initial]);

    const isValid = draft.property_code.trim() !== "" && draft.property_name.trim() !== "";

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isValid) return;
        onSubmit({
            kind: draft.kind,
            property_code: draft.property_code.trim(),
            property_name: draft.property_name.trim(),
            is_active: draft.is_active,
        });
    };

    return (
        <Modal
            open={open}
            onOpenChange={(next) => !next && onClose()}
            title={mode === "create" ? "Add Property Code" : "Edit Property Code"}
            description="Property code, kind, description, and status."
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                        {error}
                    </div>
                )}
                <FormField label="Kind" htmlFor="propcode-kind" required>
                    <select
                        id="propcode-kind"
                        aria-label="Kind"
                        value={draft.kind}
                        onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {KIND_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </FormField>
                <FormField label="Code" htmlFor="propcode-code" required>
                    <Input
                        id="propcode-code"
                        aria-label="Code"
                        value={draft.property_code}
                        onChange={(e) => setDraft({ ...draft, property_code: e.target.value })}
                        placeholder="Code"
                    />
                </FormField>
                <FormField label="Description" htmlFor="propcode-description" required>
                    <Input
                        id="propcode-description"
                        aria-label="Description"
                        value={draft.property_name}
                        onChange={(e) => setDraft({ ...draft, property_name: e.target.value })}
                        placeholder="Description"
                    />
                </FormField>
                <label className="inline-flex items-center gap-2">
                    <input
                        type="checkbox"
                        aria-label="Active"
                        data-testid="propcode-active-checkbox"
                        checked={draft.is_active}
                        onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                    />
                    <span>Active</span>
                </label>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        data-testid="propcode-save-button"
                        disabled={isSaving || !isValid}
                    >
                        {isSaving ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Saving...
                            </>
                        ) : mode === "create" ? (
                            "Create"
                        ) : (
                            "Save"
                        )}
                    </Button>
                </DialogFooter>
            </form>
        </Modal>
    );
}
